// frontend/src/components/parent/AiTopicsCard.tsx
// MinikBot'a sorulan konu başlıkları — okunabilir liste + bar chart

import { AiTopicsReport } from '../../types/parent.types';
import { IconBrain, IconMessageCircle } from './ParentIcons';

interface AiTopicsCardProps {
  data: AiTopicsReport | null;
  isLoading: boolean;
}

const FEATURE_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  mentor_chat: { label: 'Sohbet',  color: '#7C3AED', bg: 'rgba(124,58,237,0.1)' },
  code_eval:   { label: 'Kod',     color: '#00AEEF', bg: 'rgba(0,174,239,0.1)' },
  quiz_gen:    { label: 'Quiz',    color: '#EF4444', bg: 'rgba(239,68,68,0.1)' },
  career_path: { label: 'Kariyer', color: '#F59E0B', bg: 'rgba(245,158,11,0.1)' },
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
}

const RANK_COLORS = ['#7C3AED', '#00AEEF', '#10C27A', '#F59E0B', '#EF4444'];

export function AiTopicsCard({ data, isLoading }: AiTopicsCardProps) {
  const maxCount = Math.max(data?.topics[0]?.count ?? 1, 1);

  return (
    <div className="pcd-section-card pcd-ai-topics-card">
      <div className="pcd-section-header">
        <div className="pcd-section-indicator" style={{ background: '#7C3AED' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
          <h3 className="pcd-section-title">MinikBot'a Sorulan Konular</h3>
          <span className="pcd-topics-period-label">Son 30 Gün</span>
        </div>
        {data && (
          <span className="pcd-topics-total-badge">
            <IconMessageCircle size={13} />
            {data.totalSessions} sohbet
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="pcd-topics-list">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="pcd-topic-row pcd-skeleton-row" />
          ))}
        </div>
      ) : !data || data.topics.length === 0 ? (
        <div className="pcd-ai-empty">
          <IconBrain size={40} style={{ color: '#CBD5E1' }} />
          <p style={{ textAlign: 'center', color: '#94A3B8', lineHeight: 1.6 }}>
            Henüz MinikBot ile konu bazlı konuşma yapılmamış.<br />
            <span style={{ fontSize: 12 }}>
              Öğrenci AI Mentor'u kullandıkça sorular burada görünecek.
            </span>
          </p>
        </div>
      ) : (
        <div className="pcd-topics-list">
          {data.topics.map((topic, idx) => {
            const barWidth = Math.max(6, (topic.count / maxCount) * 100);
            const fc = FEATURE_CONFIG[topic.feature] ?? FEATURE_CONFIG.mentor_chat;
            const rankColor = RANK_COLORS[idx % RANK_COLORS.length];
            return (
              <div className="pcd-topic-row" key={idx}>
                {/* Sıra numarası */}
                <div className="pcd-topic-rank-badge" style={{ background: rankColor }}>
                  {idx + 1}
                </div>

                {/* Konu adı + bar */}
                <div className="pcd-topic-info">
                  <div className="pcd-topic-name" title={topic.topic}>
                    {topic.topic}
                  </div>
                  <div className="pcd-topic-bar-row">
                    <div className="pcd-topic-bar-wrap">
                      <div
                        className="pcd-topic-bar"
                        style={{ width: `${barWidth}%`, background: rankColor }}
                      />
                    </div>
                    <span className="pcd-topic-count" style={{ color: rankColor }}>
                      {topic.count} kez
                    </span>
                  </div>
                </div>

                {/* Meta bilgiler */}
                <div className="pcd-topic-meta">
                  <span className="pcd-topic-badge"
                    style={{ color: fc.color, background: fc.bg }}>
                    {fc.label}
                  </span>
                  <span className="pcd-topic-date">{formatDate(topic.lastDate)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
